import React, { useState, useEffect, useCallback } from 'react';
import { Check, ChevronRight, Users, Building, Star, StarHalf } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import StudentSidebar from '../components/StudentSidebar.js';
import Footer from '../components/Footer.js';
import Nav from '../components/Nav.js';
import ImageWithFallback from '../components/ImageWithFallback.js';
import { getCourses, getFlashSale, UPLOAD_URL, getMediaUrl, trackPageVisit } from '../services/api';
import { useAuth } from '../context/AuthContext';

// Moved OUTSIDE the Landing component so it doesn't get recreated on every re-render
const generateStars = (rating) => {
    const validRating = Math.max(0, Math.min(5, Number(rating) || 0));
    const fullStars = Math.floor(validRating);
    const hasHalfStar = validRating % 1 !== 0;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

    return (
        <div className="flex items-center gap-0.5 text-yellow-500">
            {[...Array(fullStars)].map((_, i) => <Star key={`full-${i}`} className="w-3 h-3 sm:w-3.5 sm:h-3.5" fill="currentColor" />)}
            {hasHalfStar && <StarHalf className="w-3 h-3 sm:w-3.5 sm:h-3.5" fill="currentColor" />}
            {[...Array(emptyStars)].map((_, i) => <Star key={`empty-${i}`} className="w-3 h-3 sm:w-3.5 sm:h-3.5" />)}
        </div>
    );
};

// Moved OUTSIDE the Landing component to prevent remounting on every countdown tick
const CourseCard = ({ course, onCourseClick }) => {
    return (
        <div
            onClick={() => onCourseClick(course)}
            className="bg-white border border-gray-200/90 rounded-xl overflow-hidden transition-all duration-300 hover:shadow-md hover:-translate-y-1 hover:border-emerald-500 cursor-pointer group flex flex-col justify-between"
        >
            <div>
                <div className="relative overflow-hidden aspect-[16/9] bg-gray-100">
                    <ImageWithFallback
                        src={getMediaUrl(course.image)}
                        alt={course.title}
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    />
                    {course.discount > 0 && (
                        <span className="absolute top-1.5 right-1.5 px-1 py-0.5 text-[9px] sm:text-xs font-bold text-white bg-red-500 rounded shadow">
                            {course.discount}% off
                        </span>
                    )}
                </div>
                <div className="p-2.5 sm:p-4">
                    <h3 className="text-xs sm:text-sm md:text-base font-bold text-gray-900 line-clamp-2 leading-tight mb-1 group-hover:text-emerald-700 transition-colors">
                        {course.title}
                    </h3>
                    <p className="text-[10px] sm:text-xs text-gray-500 mb-1 truncate">{course.instructor}</p>
                    <div className="flex items-center gap-1 mb-1 flex-wrap">
                        {generateStars(course.rating)}
                        <span className="font-bold text-[10px] sm:text-xs text-gray-800">{course.rating}</span>
                        <span className="text-[9px] sm:text-xs text-gray-400">({(course.ratingCount || 0).toLocaleString()})</span>
                    </div>
                    <div className="flex items-baseline gap-1 mb-1">
                        <span className="text-xs sm:text-base md:text-lg font-extrabold text-gray-900">₹{course.price}</span>
                        {course.originalPrice > course.price && (
                            <span className="text-[10px] sm:text-xs text-gray-400 line-through">₹{course.originalPrice}</span>
                        )}
                    </div>
                </div>
            </div>
            <div className="px-2.5 sm:px-4 pb-2 pt-1.5 border-t border-gray-100 flex items-center justify-between text-[9px] sm:text-xs text-gray-500">
                <span className="flex items-center gap-0.5 truncate">
                    <i className="far fa-clock text-gray-400 text-[9px] sm:text-xs" /> {course.duration}
                </span>
                <span className="flex items-center gap-0.5 shrink-0">
                    <i className="fas fa-play-circle text-gray-400 text-[9px] sm:text-xs" /> {course.lectures} lectures
                </span>
            </div>
        </div>
    );
};

const Landing = () => {

    const { isLoggedIn } = useAuth();

    const [activeCategory, setActiveCategory] = useState('all');

    const [authModalOpen, setAuthModalOpen] = useState(false);
    const [countdown, setCountdown] = useState({ hours: 0, minutes: 0, seconds: 0 });
    const [activeFlashSale, setActiveFlashSale] = useState(null);
    const [recommendedCourses, setRecommendedCourses] = useState([]);
    const [popularCourses, setPopularCourses] = useState([]);
    const [filteredCourses, setFilteredCourses] = useState([]);
    const [allCourses, setAllCourses] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isError, setIsError] = useState(false);

    const [openDropdown, setOpenDropdown] = useState(null);
    const [showStudentNavbar, setShowStudentNavbar] = useState(false);
    const [departmentsWithCourses, setDepartmentsWithCourses] = useState([]);
    const [ailableDepartments, setAvailableDepartments] = useState([]);

    const navigate = useNavigate();

    useEffect(() => {
        trackPageVisit({ page_name: 'Landing Page (Home)', page_url: '/' });
    }, []);

    // Function to load departments from courses
    const processDepartments = (courses) => {
        const depts = {};
        courses.forEach(course => {
            const dept = course.department || 'General';
            if (!depts[dept]) {
                depts[dept] = { department: dept, courses: [], courseCount: 0 };
            }
            depts[dept].courses.push(course);
            depts[dept].courseCount++;
        });
        const deptList = Object.values(depts);
        setDepartmentsWithCourses(deptList);
        setAvailableDepartments(deptList.map(d => d.department));
    };


    useEffect(() => {
        const query = new URLSearchParams(window.location.search);
        const orderId = query.get('order_id');
        const courseId = query.get('course_id');
        if (orderId) {
            const courseParam = courseId ? `&course_id=${courseId}` : '';
            navigate(`/courseoverview?order_id=${orderId}${courseParam}`);
        }
    }, [navigate]);

    useEffect(() => {
        const fetchAllData = async () => {
            setIsLoading(true);
            setIsError(false);
            try {
                // Fetch active flash sale
                try {
                    const flashRes = await getFlashSale();
                    if (flashRes.success && flashRes.active) {
                        setActiveFlashSale(flashRes.data);
                    } else {
                        setActiveFlashSale(null);
                    }
                } catch (err) {
                    console.error("Failed to fetch flash sale details:", err);
                }

                const response = await getCourses();
                if (response.success) {
                    const data = response.data;
                    setAllCourses(data);
                    setRecommendedCourses(data.filter(course => course.recommended));
                    setPopularCourses(data);
                    setFilteredCourses(data);
                    processDepartments(data);
                } else {
                    setIsError(true);
                }
            } catch (error) {
                console.error("Failed to fetch courses:", error);
                setIsError(true);
            } finally {
                setIsLoading(false);
            }
        };

        fetchAllData();
    }, []);

    // Live ticking countdown timer based on active flash sale end_time
    useEffect(() => {
        if (!activeFlashSale || !activeFlashSale.endTime) return;

        const updateCountdown = () => {
            const end = new Date(activeFlashSale.endTime);
            const now = new Date();
            const diff = end - now;

            if (diff <= 0) {
                setCountdown({ hours: 0, minutes: 0, seconds: 0 });
                setActiveFlashSale(null);
            } else {
                const hours = Math.floor(diff / (1000 * 60 * 60));
                const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
                const seconds = Math.floor((diff % (1000 * 60)) / 1000);
                setCountdown({ hours, minutes, seconds });
            }
        };

        updateCountdown();
        const timer = setInterval(updateCountdown, 1000);

        return () => clearInterval(timer);
    }, [activeFlashSale]);

    // Listen for course updates
    useEffect(() => {
        const handleCoursesUpdate = () => {
            const fetchUpdatedData = async () => {
                try {
                    const response = await getCourses();
                    if (response.success) {
                        const data = response.data;
                        setAllCourses(data);
                        setFilteredCourses(data);
                        processDepartments(data);

                        if (activeCategory !== 'all') {
                            const filtered = data.filter(course =>
                                course.category?.toLowerCase().includes(activeCategory.toLowerCase()) ||
                                course.department?.toLowerCase().includes(activeCategory.toLowerCase())
                            );
                            setFilteredCourses(filtered);
                        }
                    }
                } catch (error) {
                    console.error("Failed to update courses:", error);
                }
            };
            fetchUpdatedData();
        };

        window.addEventListener('coursesUpdated', handleCoursesUpdate);
        return () => window.removeEventListener('coursesUpdated', handleCoursesUpdate);
    }, [activeCategory]);


    const handleCategoryClick = (category, shouldScroll = true) => {
        setActiveCategory(category);
        if (category === 'all') {
            setFilteredCourses(allCourses);
            setRecommendedCourses(allCourses.filter(course => course.recommended));
            setPopularCourses(allCourses);
        } else {
            const filtered = allCourses.filter(course =>
                course.category?.toLowerCase().includes(category.toLowerCase()) ||
                course.department?.toLowerCase().includes(category.toLowerCase())
            );
            setFilteredCourses(filtered);
            setRecommendedCourses(filtered.filter(course => course.recommended));
            setPopularCourses(filtered);
        }

        // ⭐ Smooth scroll to results only if requested
        if (shouldScroll) {
            setTimeout(() => {
                const resultsSection = document.getElementById('recommended') || document.getElementById('popular');
                if (resultsSection) {
                    const navHeight = 80;
                    const elementPosition = resultsSection.getBoundingClientRect().top;
                    const offsetPosition = elementPosition + window.pageYOffset - navHeight;

                    window.scrollTo({
                        top: offsetPosition,
                        behavior: "smooth"
                    });
                }
            }, 100);
        }
    };


    // Add this state to your component
    const [clickedDropdown, setClickedDropdown] = useState(false);

    useEffect(() => {
        const handleClickOutside = (event) => {
            // Check if click is outside any dropdown container
            const isOutsideDropdown = !event.target.closest('.relative.group');
            if (isOutsideDropdown && openDropdown) {
                setOpenDropdown(null);
                setClickedDropdown(false);
            }
        };

        // Handle touch events for mobile
        const handleTouchOutside = (event) => {
            const isOutsideDropdown = !event.target.closest('.relative.group');
            if (isOutsideDropdown && openDropdown) {
                setOpenDropdown(null);
                setClickedDropdown(false);
            }
        };

        // Add event listeners
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('touchstart', handleTouchOutside);

        // Clean up event listeners
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleTouchOutside);
        };
    }, [openDropdown, clickedDropdown]);

    const handleCourseClick = useCallback((course) => {
        if (!course) return;
        localStorage.setItem('selectedCourse', JSON.stringify(course));
        navigate(`/courseoverview?course_id=${course.id}`);
    }, [navigate]);


    return (
        <div className="min-h-screen bg-white overflow-x-hidden">
            {/* Navbar */}
            <Nav setShowStudentNavbar={setShowStudentNavbar} />

            {/* Mobile overlay sidebar */}
            {showStudentNavbar && (
                <StudentSidebar onClose={() => setShowStudentNavbar(false)} />
            )}

            {/* Main Content */}
            <main className="pt-14 sm:pt-16 max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 transition-all duration-300">
                {/* Categories - DYNAMIC */}
                <section className="pt-3 sm:pt-6 pb-2 sm:pb-3 bg-gradient-to-r from-emerald-50/80 to-teal-50/80 -mx-3 sm:-mx-6 lg:-mx-8 px-3 sm:px-6 lg:px-8 rounded-b-2xl mb-4 sm:mb-6 relative z-30 overflow-visible">
                    <div className="max-w-7xl mx-auto">
                        {/* Only show categories section if there are courses */}
                        {isLoading ? (
                            <div className="text-center py-24">
                                <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-emerald-500 border-t-transparent"></div>
                                <p className="mt-4 text-gray-600 font-medium animate-pulse">Fetching latest courses...</p>
                            </div>
                        ) : isError ? (
                            <div className="text-center py-12">
                                <div className="max-w-md mx-auto">
                                    <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
                                        <i className="fas fa-exclamation-triangle text-red-400 text-3xl"></i>
                                    </div>
                                    <h3 className="text-xl font-semibold text-gray-800 mb-2">Connection Error</h3>
                                    <p className="text-gray-600 mb-6">
                                        We're having trouble connecting to the server. Please check if the backend is running.
                                    </p>
                                    <button
                                        onClick={() => window.location.reload()}
                                        className="bg-emerald-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-emerald-700 transition-colors inline-flex items-center gap-2"
                                    >
                                        <i className="fas fa-sync"></i>
                                        Try Again
                                    </button>
                                </div>
                            </div>
                        ) : allCourses.length > 0 ? (
                            <>
                                <div className="flex flex-nowrap overflow-x-auto hide-scrollbar md:overflow-visible md:flex-wrap justify-start md:justify-center gap-1.5 sm:gap-3 w-full py-1 relative z-30">
                                    {/* All Courses Button */}
                                    <button
                                        onClick={() => {
                                            handleCategoryClick('all', true);
                                            setOpenDropdown(null);
                                            setClickedDropdown(false);
                                        }}
                                        className={`shrink-0 whitespace-nowrap group relative px-3.5 sm:px-5 py-1.5 sm:py-2.5 rounded-full sm:rounded-xl font-semibold transition-all duration-200 text-xs sm:text-sm ${activeCategory === 'all'
                                            ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white shadow-md'
                                            : 'bg-white text-gray-700 hover:shadow-sm border border-gray-200 hover:border-emerald-200'
                                            }`}
                                    >
                                        All Courses ({allCourses.length})
                                    </button>

                                    {/* Dynamic Department Buttons */}
                                    {departmentsWithCourses.map((dept) => (
                                        <div
                                            key={dept.department}
                                            className="shrink-0 relative group"
                                            onMouseEnter={() => {
                                                if (window.innerWidth >= 768) {
                                                    setOpenDropdown(dept.department);
                                                }
                                            }}
                                            onMouseLeave={() => {
                                                if (window.innerWidth >= 768 && !clickedDropdown) {
                                                    setOpenDropdown(null);
                                                }
                                            }}
                                        >
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleCategoryClick(dept.department, false);
                                                    if (openDropdown === dept.department && clickedDropdown) {
                                                        setOpenDropdown(null);
                                                        setClickedDropdown(false);
                                                    } else {
                                                        setOpenDropdown(dept.department);
                                                        setClickedDropdown(true);
                                                    }
                                                }}
                                                className={`whitespace-nowrap group relative px-3 sm:px-5 py-1.5 sm:py-2.5 rounded-full sm:rounded-xl font-semibold transition-all duration-200 bg-white text-gray-700 hover:shadow-sm border text-xs sm:text-sm flex items-center gap-1.5 ${
                                                    activeCategory === dept.department ? 'border-emerald-500 shadow-md ring-1 ring-emerald-400 text-emerald-800 font-bold' : 'border-gray-200 hover:border-emerald-200'
                                                }`}
                                            >
                                                <span>{dept.department} ({dept.courseCount})</span>
                                                <span className={`text-[10px] transition-transform duration-200 ${openDropdown === dept.department ? 'rotate-180 text-emerald-600' : 'text-gray-400'}`}>▼</span>
                                            </button>

                                            {/* Dropdown - Works on both Mobile and Desktop */}
                                            {openDropdown === dept.department && (
                                                <div
                                                    className="absolute top-full left-1/2 sm:left-0 transform -translate-x-1/2 sm:translate-x-0 mt-2 w-[280px] sm:w-80 bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden z-50 origin-top transition-all duration-300 ease-in-out animate-in slide-in-from-top-2 fade-in-0"
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    <div className="p-2">
                                                        {dept.courses.map((course, index) => (
                                                            <button
                                                                key={course.id}
                                                                onClick={() => {
                                                                    // Save course data to localStorage and navigate to course overview
                                                                    localStorage.setItem('selectedCourse', JSON.stringify(course));
                                                                    navigate('/courseoverview');
                                                                    setOpenDropdown(null);
                                                                    setClickedDropdown(false);
                                                                }}
                                                                className={`w-full text-left px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg hover:bg-emerald-50 transition-all duration-200 ease-in-out text-xs sm:text-sm transform hover:translate-x-1 cursor-pointer animate-slide-in-left ${activeCategory === course.title ? 'bg-emerald-50 text-emerald-700 font-semibold' : 'text-gray-700'
                                                                    }`}
                                                                style={{
                                                                    animationDelay: `${index * 30}ms`,
                                                                }}
                                                            >
                                                                <span className="line-clamp-2">{course.title}</span>
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>

                                {/* Mobile Dropdown Drawer for Touch Screens */}
                                {openDropdown && (
                                    <div 
                                        className="md:hidden mt-2 bg-white rounded-xl p-2.5 shadow-xl border border-emerald-200 z-[100] animate-in slide-in-from-top-1 fade-in duration-200"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <div className="flex items-center justify-between px-2 py-1 mb-1 border-b border-gray-100 text-xs font-bold text-emerald-800">
                                            <span>{openDropdown} Courses</span>
                                            <button 
                                                onClick={() => { setOpenDropdown(null); setClickedDropdown(false); }}
                                                className="w-5 h-5 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 hover:bg-gray-200 text-[10px]"
                                            >
                                                ✕
                                            </button>
                                        </div>
                                        <div className="max-h-60 overflow-y-auto space-y-1">
                                            {departmentsWithCourses.find(d => d.department === openDropdown)?.courses.map((course) => (
                                                <button
                                                    key={course.id}
                                                    onClick={() => {
                                                        localStorage.setItem('selectedCourse', JSON.stringify(course));
                                                        navigate('/courseoverview');
                                                        setOpenDropdown(null);
                                                        setClickedDropdown(false);
                                                    }}
                                                    className="w-full text-left px-3 py-2 rounded-lg bg-gray-50/80 hover:bg-emerald-50 text-xs text-gray-800 font-medium flex items-center justify-between gap-2 border border-gray-100"
                                                >
                                                    <span className="truncate">{course.title}</span>
                                                    <span className="text-emerald-600 font-bold shrink-0">₹{course.price}</span>
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="text-center py-12">
                                <div className="max-w-md mx-auto">
                                    <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                        <i className="fas fa-book text-gray-400 text-3xl"></i>
                                    </div>
                                    <h3 className="text-xl font-semibold text-gray-800 mb-2">No Courses Available</h3>
                                    <p className="text-gray-600 mb-6">
                                        Courses will appear here once they are added by the administrator.
                                    </p>

                                </div>
                            </div>
                        )}
                    </div>
                </section>

                {/* Flash Sale Hero */}
                {activeFlashSale && (
                    <section className="my-8 relative cursor-pointer" id="trending" onClick={() => handleCourseClick(activeFlashSale)}>
                        <div className="relative h-[200px] sm:h-[400px] rounded-2xl sm:rounded-3xl overflow-hidden shadow-2xl group">
                            <ImageWithFallback
                                src="/img/flash-sale.jpg"
                                alt="Flash Sale"
                                fallbackSrc="https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=1200&h=600&fit=crop"
                                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                            />
                            <div className="absolute inset-0 bg-gradient-to-r from-black/80 to-transparent" />
                            <div className="absolute top-1/2 left-4 sm:left-12 transform -translate-y-1/2 max-w-[90%] sm:max-w-xl text-white">
                                <h2 className="text-xl sm:text-5xl font-extrabold mb-1 sm:mb-4 tracking-tight">E-Learn Flash Sale!</h2>
                                <p className="text-[10px] sm:text-lg mb-3 sm:mb-8 line-clamp-2 sm:line-clamp-none opacity-95 font-semibold text-emerald-400">
                                    Premium Course <span className="underline text-white font-bold">{activeFlashSale.title}</span> starting at just ₹{activeFlashSale.price}. Limited time offer!
                                </p>
                                <div className="flex gap-4 sm:gap-8">
                                    <div className="text-center">
                                        <div className="text-2xl sm:text-5xl font-black text-emerald-400 leading-none">
                                            {String(countdown.hours).padStart(2, '0')}
                                        </div>
                                        <div className="text-[8px] sm:text-xs font-bold uppercase mt-1 sm:mt-2 tracking-widest text-emerald-100/60">Hrs</div>
                                    </div>
                                    <div className="text-center">
                                        <div className="text-2xl sm:text-5xl font-black text-emerald-400 leading-none">
                                            {String(countdown.minutes).padStart(2, '0')}
                                        </div>
                                        <div className="text-[8px] sm:text-xs font-bold uppercase mt-1 sm:mt-2 tracking-widest text-emerald-100/60">Min</div>
                                    </div>
                                    <div className="text-center">
                                        <div className="text-2xl sm:text-5xl font-black text-emerald-400 leading-none">
                                            {String(countdown.seconds).padStart(2, '0')}
                                        </div>
                                        <div className="text-[8px] sm:text-xs font-bold uppercase mt-1 sm:mt-2 tracking-widest text-emerald-100/60">Sec</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                )}

                {/* Recommended - Only show if there are courses */}
                {recommendedCourses.length > 0 && (
                    <section className="my-5 sm:my-10" id="recommended">
                        <div className="mb-3 sm:mb-6">
                            <h2 className="text-lg sm:text-2xl md:text-3xl font-bold text-gray-900">
                                {activeCategory === 'all' ? 'Recommended For You' : `Recommended ${activeCategory} Courses`}
                            </h2>
                            <p className="text-xs sm:text-sm text-gray-500">{activeCategory === 'all' ? 'Pick the best fit' : `Top picks in ${activeCategory}`}</p>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2.5 sm:gap-6">
                            {recommendedCourses.map(course => (
                                <CourseCard key={course.id} course={course} onCourseClick={handleCourseClick} />
                            ))}
                        </div>
                    </section>
                )}

                {/* Topics - Only show if there are courses */}
                {allCourses.length > 0 && (
                    <section className="my-5 sm:my-8">
                        <div className="mb-3 sm:mb-6">
                            <h2 className="text-lg sm:text-2xl md:text-3xl font-bold text-gray-900">Explore Topics</h2>
                            <p className="text-xs sm:text-sm text-gray-500">Discover what interests you</p>
                        </div>
                        <div className="flex flex-nowrap overflow-x-auto hide-scrollbar md:flex-wrap justify-start md:justify-center gap-2 pb-2 w-full">
                            {/* All Courses Button */}
                            <button
                                onClick={() => handleCategoryClick('all')}
                                className={`shrink-0 whitespace-nowrap px-3.5 py-1.5 sm:px-5 sm:py-2.5 border rounded-full font-medium transition-all duration-200 text-xs sm:text-sm ${activeCategory === 'all'
                                    ? 'bg-emerald-600 text-white border-emerald-600 shadow-md'
                                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-emerald-50 hover:border-emerald-300'
                                    }`}
                            >
                                All Courses ({allCourses.length})
                            </button>

                            {[...new Set(allCourses.map(course => course.department))].map(topic => (
                                <button
                                    key={topic}
                                    onClick={() => handleCategoryClick(topic)}
                                    className={`shrink-0 whitespace-nowrap px-3.5 py-1.5 sm:px-5 sm:py-2.5 border rounded-full font-medium transition-all duration-200 text-xs sm:text-sm ${activeCategory === topic
                                        ? 'bg-emerald-600 text-white border-emerald-600 shadow-md'
                                        : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-emerald-50 hover:border-emerald-300'
                                        }`}
                                >
                                    {topic} ({allCourses.filter(c => c.department === topic).length})
                                </button>
                            ))}
                        </div>
                    </section>
                )}

                {/* Popular - Only show if there are courses */}
                {popularCourses.length > 0 && (
                    <section className="my-5 sm:my-10" id="popular">
                        <div className="mb-3 sm:mb-6">
                            <h2 className="text-lg sm:text-2xl md:text-3xl font-bold text-gray-900">
                                {activeCategory === 'all' ? 'Most Popular Courses' : `${activeCategory} Courses`}
                            </h2>
                            <p className="text-xs sm:text-sm text-gray-500">{activeCategory === 'all' ? 'Join millions of learners' : `Explore all courses in ${activeCategory}`}</p>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2.5 sm:gap-6">
                            {popularCourses.map(course => (
                                <CourseCard key={course.id} course={course} onCourseClick={handleCourseClick} />
                            ))}
                        </div>
                    </section>
                )}

                {/* Empty State - Only show if there are no courses and not loading/error */}
                {!isLoading && !isError && allCourses.length === 0 && (
                    <section className="my-16">
                        <div className="text-center py-16">
                            <div className="max-w-lg mx-auto">
                                <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                                    <i className="fas fa-graduation-cap text-gray-400 text-4xl"></i>
                                </div>
                                <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-4 text-center">Start Your Learning Journey</h2>
                                <p className="text-sm sm:text-base text-gray-600 mb-8 px-4 text-center">
                                    No courses are available at the moment. Please check back later or contact your administrator to add courses.
                                </p>
                                <div className="flex gap-4 justify-center">

                                    <button
                                        onClick={() => setAuthModalOpen(true)}
                                        className="border-2 border-emerald-600 text-emerald-600 px-8 py-3 rounded-lg font-semibold hover:bg-emerald-50 transition-colors inline-flex items-center gap-2"
                                    >
                                        <i className="fas fa-bell"></i>
                                        Get Notified
                                    </button>
                                </div>
                            </div>
                        </div>
                    </section>
                )}

                {/* Affiliations & Accreditations Section */}
                <section className="my-12 bg-gradient-to-r from-gray-50 via-white to-gray-50 rounded-2xl sm:rounded-3xl py-10 px-6 sm:px-8 border border-gray-100 shadow-sm animate-fadeIn">
                    <div className="text-center mb-8">
                        <h2 className="text-xs sm:text-sm font-bold text-gray-400 uppercase tracking-widest mb-2">Our Affiliations & Accreditations</h2>
                        <div className="h-1 w-16 bg-emerald-600 mx-auto rounded-full"></div>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-6 items-center justify-center">
                        {[
                            { src: "/img/Ministry of Skill Development and Entrepreneurship.png", alt: "Ministry of Skill Development and Entrepreneurship" },
                            { src: "/img/Skill_India.png", alt: "Skill India" },
                            { src: "/img/msme.jpg", alt: "MSME" },
                            { src: "/img/national skill development corporation.jpg", alt: "National Skill Development Corporation" },
                            { src: "/img/skill management and accreditation of training centres.jpg", alt: "Skill Management and Accreditation of Training Centres" },
                            { src: "/img/vlsi.png", alt: "VLSI" }
                        ].map((logo, idx) => (
                            <div key={idx} className="bg-white p-3 rounded-xl sm:rounded-2xl shadow-sm border border-gray-100 hover:shadow-md hover:border-emerald-300 transition-all duration-300 transform hover:-translate-y-1 flex items-center justify-center h-24">
                                <img
                                    src={logo.src}
                                    alt={logo.alt}
                                    className="max-h-16 max-w-full object-contain filter transition-all duration-300"
                                />
                            </div>
                        ))}
                    </div>
                </section>
            </main>

            {/* Footer */}
            <Footer />

            {/* Notification Modal */}
            {authModalOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-300">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8 relative animate-in zoom-in-95 duration-300">
                        <button
                            onClick={() => setAuthModalOpen(false)}
                            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
                        >
                            <i className="fas fa-times text-xl"></i>
                        </button>
                        <div className="text-center">
                            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <i className="fas fa-bell text-emerald-600 text-2xl"></i>
                            </div>
                            <h3 className="text-2xl font-bold text-gray-900 mb-2">Get Notified</h3>
                            <p className="text-gray-600 mb-6">
                                Enter your email to be the first to know when new courses are added to our platform.
                            </p>
                            <form onSubmit={(e) => {
                                e.preventDefault();
                                alert('Thank you! You will be notified.');
                                setAuthModalOpen(false);
                            }}>
                                <input
                                    type="email"
                                    required
                                    placeholder="Enter your email"
                                    className="w-full px-4 py-3 rounded-lg border-2 border-gray-100 focus:border-emerald-500 focus:ring-0 transition-all mb-4 outline-none"
                                />
                                <button
                                    type="submit"
                                    className="w-full bg-emerald-600 text-white font-bold py-3 rounded-lg hover:bg-emerald-700 transition-all shadow-lg hover:shadow-emerald-200"
                                >
                                    Subscribe Now
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Landing;